/**
 * useWarRoomMetrics Hook (MA-BE-082)
 * 
 * Custom hook to fetch War Room metrics from the canonical metrics engine.
 * 
 * Endpoints used:
 * - /api/war-room/summary - Complete War Room summary for KPI cards
 * - /api/war-room/profit-pulse - Profit Pulse metrics
 * - /api/war-room/conversion-metrics - Conversion funnel metrics
 * - /api/war-room/funnel-economics-waterfall - Waterfall visualization data
 * - /api/war-room/comparison - Period comparison with deltas
 * 
 * Metric Confidence Standard:
 * - Financially significant metrics return MetricValue objects with {value, status, reason}
 * - Status: exact | estimated | partial_data | missing_data | not_applicable
 * - UI should handle null values and display confidence indicators
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * Calculate date range based on period
 */
const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  let startDate;
  let endDate = now.toISOString();
  
  switch (period) {
    case '7d':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 7);
      break;
    case '30d':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 30);
      break;
    case 'mtd':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'ytd':
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  }
  
  return {
    start_date: startDate.toISOString(),
    end_date: endDate
  };
};

/**
 * Extract value from MetricValue object or raw value
 * Handles both {value, status, reason} structure and legacy simple values
 */
const extractMetricValue = (metric) => {
  if (metric === null || metric === undefined) return null;
  if (typeof metric === 'object' && 'value' in metric) {
    return metric.value;
  }
  return metric;
};

/**
 * Extract confidence status from MetricValue object
 */
const extractMetricStatus = (metric) => {
  if (metric === null || metric === undefined) return 'missing_data';
  if (typeof metric === 'object' && 'status' in metric) {
    return metric.status;
  }
  return 'exact'; // Legacy values assumed exact
};

/**
 * Transform summary response to KPI format expected by existing UI
 */
const transformSummaryToKPIs = (summary, comparison) => {
  if (!summary) return {};
  
  const deltas = comparison?.deltas || {};
  
  // Helper to determine direction from delta
  const getDirection = (delta) => {
    if (delta === null || delta === undefined) return 'flat';
    return delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  };
  
  return {
    leads_generated: {
      value: summary.total_leads,
      change_percent: deltas.leads,
      direction: getDirection(deltas.leads),
      status: 'exact'
    },
    // MA-IMPL-119A: Appointment count for Conversion Flow
    appointments_count: {
      value: summary.total_appointments,
      change_percent: deltas.appointments,
      direction: getDirection(deltas.appointments),
      status: 'exact'
    },
    lead_to_booked_rate: {
      value: summary.lead_to_appointment_rate,
      change_percent: deltas.lead_to_appointment_rate,
      direction: getDirection(deltas.lead_to_appointment_rate),
      status: 'exact'
    },
    speed_to_book_median_hours: {
      value: extractMetricValue(summary.speed_to_book_hours),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.speed_to_book_hours),
      reason: summary.speed_to_book_hours?.reason
    },
    show_rate: {
      value: extractMetricValue(summary.show_rate),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.show_rate),
      reason: summary.show_rate?.reason
    },
    // MA-FIX-122: Shows count - use total_shows directly from backend
    shows_count: {
      value: summary.total_shows ?? 0,
      change_percent: null,
      direction: 'flat',
      status: summary.total_shows !== undefined ? 'exact' : 'missing_data',
      reason: 'Appointments where lead showed (completed)'
    },
    // MA-IMPL-119A: Client count for Conversion Flow
    clients_count: {
      value: summary.total_clients,
      change_percent: deltas.clients,
      direction: getDirection(deltas.clients),
      status: 'exact'
    },
    sales_conversion_rate: {
      value: summary.sales_conversion_rate,
      change_percent: deltas.sales_conversion_rate,
      direction: getDirection(deltas.sales_conversion_rate),
      status: 'exact'
    },
    issue_rate: {
      value: extractMetricValue(summary.issue_rate),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.issue_rate),
      reason: summary.issue_rate?.reason
    },
    premium_written: {
      value: summary.premium_written,
      change_percent: deltas.premium_written,
      direction: getDirection(deltas.premium_written),
      status: 'exact'
    },
    commission_generated: {
      value: summary.commission,
      change_percent: null,
      direction: 'flat',
      status: 'exact'
    },
    revenue_per_lead: {
      value: extractMetricValue(summary.revenue_per_lead),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.revenue_per_lead),
      reason: summary.revenue_per_lead?.reason
    },
    // MA-IMPL-119A: Revenue per Appointment for Conversion Flow
    revenue_per_appointment: {
      value: summary.total_appointments > 0 ? (summary.commission || 0) / summary.total_appointments : null,
      change_percent: null,
      direction: 'flat',
      status: summary.total_appointments > 0 ? 'exact' : 'insufficient_data',
      reason: summary.total_appointments > 0 ? 'Commission / Appointments' : 'No appointments in period'
    },
    cost_per_lead: {
      value: extractMetricValue(summary.cost_per_lead),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.cost_per_lead),
      reason: summary.cost_per_lead?.reason
    },
    cost_per_acquisition: {
      value: extractMetricValue(summary.cost_per_appointment),
      change_percent: null,
      direction: 'flat',
      status: extractMetricStatus(summary.cost_per_appointment),
      reason: summary.cost_per_appointment?.reason
    },
    net_profit: {
      value: summary.net_profit,
      change_percent: deltas.net_profit,
      direction: getDirection(deltas.net_profit),
      status: 'exact'
    },
    // MA-IMPL-119A: Deployable rate for Executive Layer
    deployable_rate: {
      value: summary.deployable_rate?.value || summary.deployable_rate,
      change_percent: null,
      direction: 'flat',
      status: summary.deployable_rate?.value ? 'exact' : 'insufficient_data',
      reason: 'Percent of leads eligible for outreach'
    }
  };
};

/**
 * Transform waterfall response to funnel format expected by FunnelVisualization
 */
const transformWaterfallToFunnel = (waterfall) => {
  if (!waterfall) return [];
  
  return [
    {
      stage: 'Visitors',
      count: waterfall.visitors || 0,
      conversion_rate: waterfall.visitor_to_lead_rate || 0
    },
    {
      stage: 'Leads',
      count: waterfall.leads || 0,
      conversion_rate: waterfall.lead_to_appointment_rate || 0
    },
    {
      stage: 'Appointments',
      count: waterfall.appointments || 0,
      conversion_rate: waterfall.appointment_to_client_rate || 0
    },
    {
      stage: 'Clients',
      count: waterfall.clients || 0,
      conversion_rate: 1.0
    }
  ];
};

/**
 * Main hook for War Room metrics
 */
export function useWarRoomMetrics(options = {}) {
  const {
    period = 'mtd',
    workspaceId,
    campaignId,
    funnelId,
    tenantId,  // MA-ANALYTICS-001: Support tenant override for SuperAdmin
    enabled = true
  } = options;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [profitPulse, setProfitPulse] = useState(null);
  const [waterfall, setWaterfall] = useState(null);
  const [comparison, setComparison] = useState(null);
  
  // Computed states for UI
  const [kpis, setKpis] = useState({});
  const [funnel, setFunnel] = useState([]);
  
  const fetchMetrics = useCallback(async () => {
    if (!enabled) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { start_date, end_date } = getDateRange(period);
      
      // Build query params
      const params = new URLSearchParams();
      params.set('start_date', start_date);
      params.set('end_date', end_date);
      if (workspaceId) params.set('workspace_id', workspaceId);
      // MA-IMPL-118: Include campaign_id in all metric fetches
      if (campaignId && campaignId !== 'all') params.set('campaign_id', campaignId);
      if (funnelId) params.set('funnel_id', funnelId);
      // MA-ANALYTICS-001: Pass tenant_id for SuperAdmin cross-tenant access
      if (tenantId) params.set('tenant_id', tenantId);
      
      const queryString = params.toString();
      
      // Build comparison query with tenant_id and campaign_id
      const comparisonParams = new URLSearchParams();
      comparisonParams.set('period', period);
      if (workspaceId) comparisonParams.set('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') comparisonParams.set('campaign_id', campaignId);
      if (tenantId) comparisonParams.set('tenant_id', tenantId);
      
      // Fetch all metrics in parallel
      const [summaryRes, profitPulseRes, waterfallRes, comparisonRes] = await Promise.all([
        api.get(`/war-room/summary?${queryString}`),
        api.get(`/war-room/profit-pulse?${queryString}`),
        api.get(`/war-room/funnel-economics-waterfall?${queryString}`),
        api.get(`/war-room/comparison?${comparisonParams.toString()}`)
      ]);
      
      // Store raw data
      setSummary(summaryRes.data);
      setProfitPulse(profitPulseRes.data);
      setWaterfall(waterfallRes.data);
      setComparison(comparisonRes.data);
      
      // Transform for UI
      setKpis(transformSummaryToKPIs(summaryRes.data, comparisonRes.data));
      setFunnel(transformWaterfallToFunnel(waterfallRes.data));
      
    } catch (err) {
      console.error('[WAR_ROOM_METRICS] Error fetching metrics:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [period, workspaceId, campaignId, funnelId, tenantId, enabled]);
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);
  
  return {
    // Loading state
    loading,
    error,
    
    // Raw data from canonical endpoints
    summary,
    profitPulse,
    waterfall,
    comparison,
    
    // Transformed data for existing UI components
    kpis,
    funnel,
    
    // Actions
    refresh: fetchMetrics
  };
}

/**
 * Hook for Profit Pulse widget specifically
 * Handles MetricValue structure with {value, status, reason}
 */
export function useProfitPulse(options = {}) {
  const {
    period = 'mtd',
    campaignId,
    funnelId,
    enabled = true
  } = options;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  const fetchProfitPulse = useCallback(async () => {
    if (!enabled) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { start_date, end_date } = getDateRange(period);
      
      const params = new URLSearchParams();
      params.set('start_date', start_date);
      params.set('end_date', end_date);
      if (campaignId) params.set('campaign_id', campaignId);
      if (funnelId) params.set('funnel_id', funnelId);
      
      const response = await api.get(`/war-room/profit-pulse?${params.toString()}`);
      setData(response.data);
      
    } catch (err) {
      console.error('[PROFIT_PULSE] Error:', err);
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [period, campaignId, funnelId, enabled]);
  
  useEffect(() => {
    fetchProfitPulse();
  }, [fetchProfitPulse]);
  
  // Extract values from MetricValue objects
  const capitalDeployedValue = extractMetricValue(data?.capital_deployed);
  const capitalEfficiencyValue = extractMetricValue(data?.capital_efficiency);
  const projectedProfitValue = extractMetricValue(data?.projected_profit);
  
  // Extract confidence status
  const capitalDeployedStatus = extractMetricStatus(data?.capital_deployed);
  const capitalEfficiencyStatus = extractMetricStatus(data?.capital_efficiency);
  
  return {
    loading,
    error,
    data,
    refresh: fetchProfitPulse,
    
    // Convenience accessors (extract values from MetricValue objects)
    totalProfit: data?.total_profit ?? 0,
    projectedProfit: projectedProfitValue,
    capitalDeployed: capitalDeployedValue,
    capitalEfficiency: capitalEfficiencyValue,
    capitalEfficiencyStatus: data?.capital_efficiency_status, // Legacy status field
    totalRevenue: data?.total_revenue ?? 0,
    totalCommission: data?.total_commission ?? 0,
    revenueEventCount: data?.revenue_event_count ?? 0,
    
    // Confidence metadata
    capitalDeployedConfidence: capitalDeployedStatus,
    capitalEfficiencyConfidence: capitalEfficiencyStatus,
    capitalDeployedReason: data?.capital_deployed?.reason,
    capitalEfficiencyReason: data?.capital_efficiency?.reason,
    
    // Status helpers
    hasCapitalData: capitalDeployedValue !== null,
    isCapitalEfficient: capitalEfficiencyValue !== null && capitalEfficiencyValue >= 1.0,
    isEstimated: capitalEfficiencyStatus === 'estimated'
  };
}

export default useWarRoomMetrics;
